import { cachedFetch } from './cachedFetch.js';
import { ACTIVE_WOMENS_SEASON } from '../data/seasonConfig';
import { readPlayerJersey } from '../data/playerIdentity.js';

const DEFAULT_ERROR = 'Statistical leaders are temporarily unavailable.';

export async function getLeaders({ type, category, signal, force = false }) {
  const normalizedType = type === 'team' ? 'team' : 'individual';
  const normalizedCategory = String(category || '').trim();

  if (!normalizedCategory) {
    throw new Error('A statistical category is required.');
  }

  const response = await cachedFetch(
    `/api/leaders?type=${encodeURIComponent(normalizedType)}` +
      `&category=${encodeURIComponent(normalizedCategory)}`,
    {
      headers: { Accept: 'application/json' },
      signal,
      force,
      maxAge: 60000,
      cache: 'no-cache',
    }
  );
  const payload = await readJson(response);

  if (!response.ok) {
    throw new Error(payload?.message || DEFAULT_ERROR);
  }

  return {
    sport: String(payload?.sport || "Women's Volleyball"),
    division: String(payload?.division || 'd1'),
    available:
      payload?.available === true &&
      payload?.provisional !== true &&
      Number(payload?.season) === ACTIVE_WOMENS_SEASON,
    availabilityReason: String(payload?.availabilityReason || '').trim(),
    type: payload?.type === 'team' ? 'team' : 'individual',
    category: normalizeCategory(payload?.category),
    categories: Array.isArray(payload?.categories)
      ? payload.categories.map(normalizeCategory).filter((item) => item.key)
      : [],
    title: String(payload?.title || '').trim(),
    updated: String(payload?.updated || '').trim(),
    throughDate: String(payload?.throughDate || '').trim(),
    season: integerOrNull(payload?.season),
    status: String(payload?.status || '').trim(),
    provisional: Boolean(payload?.provisional),
    totalsScope: String(payload?.totalsScope || ''),
    derivedPoints: Boolean(payload?.derivedPoints),
    leaders: payload?.provisional !== true && Number(payload?.season) === ACTIVE_WOMENS_SEASON && Array.isArray(payload?.leaders)
      ? payload.leaders.map(normalizeLeader).filter(Boolean)
      : [],
    summary: {
      leaderCount: integerOrNull(payload?.summary?.leaderCount) || 0,
      schoolsMatched: integerOrNull(payload?.summary?.schoolsMatched) || 0,
      sourceRows: integerOrNull(payload?.summary?.sourceRows) || 0,
      completedGames: integerOrNull(payload?.summary?.completedGames) || 0,
      gamesWithBoxscores: integerOrNull(payload?.summary?.gamesWithBoxscores) || 0,
      duplicateGamesRemoved: integerOrNull(payload?.summary?.duplicateGamesRemoved) || 0,
      coverageComplete: payload?.summary?.coverageComplete !== false,
    },
    source: String(payload?.source || '').trim(),
  };
}

function normalizeCategory(value) {
  return {
    key: String(value?.key || '').trim(),
    label: String(value?.label || '').trim(),
    statId: integerOrNull(value?.statId),
    valueLabel: String(value?.valueLabel || '').trim(),
    totalLabel: String(value?.totalLabel || '').trim(),
    valueStyle: String(value?.valueStyle || '').trim(),
    mode: value?.mode === 'totals' ? 'totals' : 'per-set',
  };
}

function normalizeLeader(value) {
  const rank = integerOrNull(value?.rank);
  const team = normalizeTeam(value?.team);

  if (!rank || !team.name || !team.slug) {
    return null;
  }

  return {
    id: String(value?.id || `${value?.type || ''}:${rank}:${team.slug}`).trim(),
    type: value?.type === 'team' ? 'team' : 'individual',
    rank,
    rankLabel: String(value?.rankLabel || `#${rank}`).trim(),
    tied: Boolean(value?.tied),
    name: String(value?.name || '').trim(),
    jersey: readPlayerJersey(value),
    classYear: String(value?.classYear || '').trim(),
    height: String(value?.height || '').trim(),
    position: String(value?.position || '').trim(),
    sets: integerOrNull(value?.sets),
    value: finiteNumberOrNull(value?.value),
    valueLabel: String(value?.valueLabel || '—').trim(),
    total: finiteNumberOrNull(value?.total),
    totalLabel: String(value?.totalLabel || '').trim(),
    team,
    raw: value?.raw && typeof value.raw === 'object' ? value.raw : {},
  };
}

function normalizeTeam(team) {
  return {
    slug: String(team?.slug || '').trim(),
    name: String(team?.name || '').trim(),
    longName: String(team?.longName || team?.name || '').trim(),
    aliases: Array.isArray(team?.aliases)
      ? team.aliases.map((value) => String(value || '').trim()).filter(Boolean)
      : [],
    logo: String(team?.logo || '').trim(),
  };
}

function integerOrNull(value) {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const number = Number(value);
  return Number.isInteger(number) ? number : null;
}

function finiteNumberOrNull(value) {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

async function readJson(response) {
  try {
    return await response.json();
  } catch {
    return null;
  }
}
